import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import Swal from "sweetalert2";
import { tentativeCost } from "./tentative";

const EMPTY_FORM = {
  basement_grey_structure_budget_sq_ft: "",
  grey_structure_budget_house_other_sq_ft: "",
  basement_grey_structure_good_sq_ft: "",
  grey_structure_good_house_other_sq_ft: "",
  budget_finishes_sq_ft: "",
  a_quality_finishes_sq_ft: "",
  premium_quality_finishes_sq_ft: "",
};

const COST_FIELDS = [
  {
    name: "basement_grey_structure_budget_sq_ft",
    label: "Basement Grey Budget",
  },
  {
    name: "grey_structure_budget_house_other_sq_ft",
    label: "Grey Structure Budget House",
  },
  {
    name: "basement_grey_structure_good_sq_ft",
    label: "Basement Grey Good",
  },
  {
    name: "grey_structure_good_house_other_sq_ft",
    label: "Grey Structure Good House",
  },
  {
    name: "budget_finishes_sq_ft",
    label: "Budget Finishes",
  },
  {
    name: "a_quality_finishes_sq_ft",
    label: "A Quality Finishes",
  },
  {
    name: "premium_quality_finishes_sq_ft",
    label: "Premium Quality Finishes",
    wide: true,
  },
];

const isCostField = (name) =>
  COST_FIELDS.some((field) => field.name === name);

const TentativeCostForm = ({ onCancel }) => {
  const { getTentativeCost, addOrUpdateTentativeCost } = tentativeCost();
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [initialData, setInitialData] = useState(null);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        await getTentativeCost((data) => {
          if (cancelled) return;
          const next = { ...EMPTY_FORM, ...(data || {}) };
          setFormData(next);
          setInitialData(next);
        });
      } catch (error) {
        if (!cancelled) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: error?.message || "Failed to load tentative cost.",
          });
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (isCostField(name) && value !== "" && Number(value) < 0) {
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleNumberKeyDown = (e) => {
    if (["-", "+", "e", "E"].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    if (initialData) {
      setFormData(initialData);
    } else {
      setFormData(EMPTY_FORM);
    }
    setErrors({});
  };

  const validateForm = () => {
    const nextErrors = {};
    COST_FIELDS.forEach(({ name }) => {
      const value = formData[name];
      if (value !== "" && Number(value) < 0) {
        nextErrors[name] = "Value cannot be negative";
      }
    });
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await addOrUpdateTentativeCost(formData);
      setInitialData(formData);
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Data has been successfully added!",
        confirmButtonText: "OK",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.message || "Failed to save tentative cost.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="h-16 w-16 animate-spin rounded-full border-t-4 border-b-4 border-[#982598]" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5 pb-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-extrabold text-[#15173d]">
            Tentative Cost
          </h2>
          <p className="text-xs text-gray-500">Construction cost per sq ft</p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className="ui-btn-cancel"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button type="submit" className="ui-btn-save" disabled={isSubmitting}>
            <Save size={15} />
            {isSubmitting ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <div className="ui-card">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {COST_FIELDS.map(({ name, label, wide }) => (
            <div key={name} className={wide ? "sm:col-span-2" : undefined}>
              <label className="ui-form-label">{label}</label>
              <input
                type="number"
                name={name}
                min="0"
                step="any"
                value={formData[name] ?? ""}
                onChange={handleChange}
                onKeyDown={handleNumberKeyDown}
                className={`ui-input-field ${
                  errors[name] ? "border-brand-error" : ""
                }`}
                placeholder="Enter value"
                dir="ltr"
              />
              {errors[name] ? (
                <p className="ui-form-error">{errors[name]}</p>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </form>
  );
};

export default TentativeCostForm;
